import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'found';
import { Box, IconButton, SxProps, Theme } from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { borderRadius, fontWeights, palette, spaces } from '../../assets/styles/themes/tokens';
import CloseIcon from '../../assets/close.svg';
import { BannerBar, BannerBarMessage } from '../Banner/BannerBar';

export interface PromoBannerProps {
  headline: string;
  body: string;
  ctaLabel: string;
  /** Internal `found` route for the CTA. Omit to render the CTA as a plain button driven by `onCtaClick`. */
  ctaHref?: string;
  /** Fires on CTA click (in addition to navigating, when `ctaHref` is set). */
  onCtaClick?: () => void;
  /** Leading icon. Defaults to an info icon. */
  icon?: ReactNode;
  /** When provided, renders a dismiss button that calls this. Omit for a non-dismissible banner. */
  onDismiss?: () => void;
  sx?: SxProps<Theme>;
}

const ctaStyles: SxProps<Theme> = {
  paddingTop: spaces.xs,
  paddingBottom: spaces.xs,
  paddingLeft: spaces.md,
  paddingRight: spaces.md,
  flexShrink: 0,
  border: 'none',
  cursor: 'pointer',
  backgroundColor: palette.common.white,
  borderRadius: borderRadius.full,
  // `typography` first, then `color`/`fontWeight` win over the variant's baked-in
  // `text.secondary` + light weight (the CTA sits on white, so it needs the brand blue).
  typography: 'body2',
  color: palette.primary.main,
  fontWeight: fontWeights.bold,
  textDecoration: 'none',
  '&:hover': {
    color: palette.primary.main,
    textDecoration: 'none',
  },
};

const PromoBanner: React.FC<PromoBannerProps> = ({
  headline,
  body,
  ctaLabel,
  ctaHref,
  onCtaClick,
  icon,
  onDismiss,
  sx,
}) => {
  const { t } = useTranslation();

  const ctaProps = ctaHref
    ? { component: Link, to: ctaHref }
    : { component: 'button' as const, type: 'button' as const };

  return (
    <BannerBar
      backgroundColor={palette.primary.main}
      color={palette.common.white}
      sx={[
        (theme) => ({
          borderRadius: borderRadius.xs,
          textAlign: 'left',
          position: 'relative',
          [theme.breakpoints.up('mobile')]: { alignItems: 'center' },
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <BannerBarMessage>
        <Box sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          gap: spaces.sm,
          // Clear the absolute dismiss button (top-right) on mobile so a long
          // headline never runs under it; desktop keeps the × in flow.
          paddingRight: onDismiss ? spaces.xl : 0,
          [theme.breakpoints.up('mobile')]: { paddingRight: 0 },
        })}>
          {icon || <InfoOutlined sx={{ flexShrink: 0 }} />}
          <Box>
            {/* `color: inherit` after `typography` keeps the banner's own text color
                (the variants bake in `text.secondary`, unreadable on the banner). */}
            <Box sx={{ typography: 'body1', color: 'inherit', fontWeight: fontWeights.bold }}>{headline}</Box>
            <Box sx={{ typography: 'body2', color: 'inherit', opacity: 0.9 }}>{body}</Box>
          </Box>
        </Box>
      </BannerBarMessage>
      <Box sx={(theme) => ({
        alignItems: 'center',
        display: 'flex',
        flexGrow: 0,
        flexShrink: 0,
        gap: spaces.xs,
        marginTop: spaces.xs,
        paddingBottom: spaces.xs,
        justifyContent: 'flex-end',
        [theme.breakpoints.up('mobile')]: {
          marginTop: 0,
          marginLeft: spaces.md,
          paddingBottom: 0,
        },
      })}>
        <Box {...ctaProps} onClick={onCtaClick} sx={ctaStyles}>
          {ctaLabel}
        </Box>
        {onDismiss && (
          <IconButton
            data-testid='dismiss-promo-banner'
            color='inherit'
            aria-label={t('Close')}
            onClick={onDismiss}
            sx={(theme) => ({
              position: 'absolute',
              top: spaces.xxs,
              right: spaces.xxs,
              [theme.breakpoints.up('mobile')]: { position: 'static' },
            })}
          >
            <CloseIcon />
          </IconButton>
        )}
      </Box>
    </BannerBar>
  );
};

export default PromoBanner;
